import * as d3 from 'd3';
import type { I18n } from '@learnforge/i18n';

export interface VennSet<K extends string = string> {
  id: 'A' | 'B' | 'C';
  labelKey: K;
  cx: number;
  cy: number;
  r: number;
}

/**
 * A single element rendered as a marker inside the diagram. `label` is a raw
 * data value (a die face, a student's name, `#3`) — never an i18n key, since
 * these come from the data itself rather than the demo's copy.
 */
export interface VennMember {
  id: string;
  label: string;
  sets: readonly ('A' | 'B' | 'C')[];
}

export interface VennConfig<K extends string = string> {
  sets: readonly VennSet<K>[];
  members: readonly VennMember[];
}

export interface VennHandle<K extends string = string> {
  element: SVGSVGElement;
  update(config: VennConfig<K>): void;
  setBusy(busy: boolean): void;
  destroy(): void;
}

export function createVenn<K extends string = string>(opts: {
  container: HTMLElement;
  i18n: I18n<K>;
  initial: VennConfig<K>;
}): VennHandle<K> {
  let config: VennConfig<K> = opts.initial;
  let selectedId: string | null = null;
  const width = 480;
  const height = 380;
  const svg = d3
    .create('svg')
    .attr('class', 'venn')
    .attr('viewBox', `0 0 ${width} ${height}`)
    .node()!;
  opts.container.appendChild(svg);

  function select(member: VennMember | null): void {
    selectedId = member?.id ?? null;
    render();
  }

  function render() {
    d3.select(svg).selectAll('*').remove();
    d3.select(svg).on('click', () => select(null));
    const g = d3.select(svg).append('g');

    g.selectAll<SVGCircleElement, VennSet<K>>('circle.venn-circle')
      .data(config.sets)
      .join('circle')
      .attr('class', 'venn-circle')
      .attr('cx', (d) => d.cx)
      .attr('cy', (d) => d.cy)
      .attr('r', (d) => d.r)
      .attr('fill-opacity', 0.25);

    // Label above the circle by default; when that point falls inside another
    // circle (the bottom set of a 3-set layout), drop the label below instead
    // so it never sits on top of the diagram.
    const labelY = (d: VennSet<K>): number => {
      const above = { x: d.cx, y: d.cy - d.r - 6 };
      const covered = config.sets.some(
        (o) => o !== d && (above.x - o.cx) ** 2 + (above.y - o.cy) ** 2 < o.r ** 2,
      );
      return covered ? d.cy + d.r + 16 : above.y;
    };
    g.selectAll('text.venn-label')
      .data(config.sets)
      .join('text')
      .attr('class', 'venn-label')
      .attr('x', (d) => d.cx)
      .attr('y', labelY)
      .attr('text-anchor', 'middle')
      .text((d) => opts.i18n.t(d.labelKey));

    const selectedPos = renderMembers(g, config, selectedId, select);
    const selectedMember =
      selectedPos && selectedId ? config.members.find((m) => m.id === selectedId) : undefined;
    if (selectedPos && selectedMember) {
      renderTooltip(g, tooltipText(selectedMember, config, opts.i18n), selectedPos, width, height);
    }
  }
  render();

  return {
    element: svg,
    update(next) {
      config = next;
      selectedId = null;
      render();
    },
    setBusy(busy) {
      svg.setAttribute('aria-busy', busy ? 'true' : 'false');
    },
    destroy() {
      svg.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Region-anchor positions (bespoke 2D point math — see design D10 for the
// exact formulas; hand-rolled rather than pulling in a geometry dependency).
// ---------------------------------------------------------------------------

interface Point {
  x: number;
  y: number;
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function sub(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y };
}

function unit(v: Point): Point {
  const len = Math.sqrt(v.x * v.x + v.y * v.y);
  return len === 0 ? { x: 0, y: 0 } : { x: v.x / len, y: v.y / len };
}

function centroid(points: readonly Point[]): Point {
  const n = points.length;
  return {
    x: points.reduce((s, p) => s + p.x, 0) / n,
    y: points.reduce((s, p) => s + p.y, 0) / n,
  };
}

function center(set: VennSet): Point {
  return { x: set.cx, y: set.cy };
}

interface RegionPosition {
  key: string;
  x: number;
  y: number;
}

/**
 * The "outside all sets" anchor — centered under the sets, far enough below
 * their lowest edge that a grid of markers has room before it starts
 * crowding the circles.
 */
function outsideAnchor<K extends string = string>(sets: readonly VennSet<K>[]): RegionPosition {
  const x = sets.reduce((s, d) => s + d.cx, 0) / sets.length;
  const yMax = Math.max(...sets.map((d) => d.cy + d.r));
  return { key: 'none', x, y: yMax + 40 };
}

function computeRegionPositions<K extends string = string>(
  sets: readonly VennSet<K>[],
): RegionPosition[] {
  if (sets.length === 3) {
    const [A, B, C] = sets as [VennSet<K>, VennSet<K>, VennSet<K>];
    const positions: RegionPosition[] = [];

    for (const [x, others] of [
      [A, [B, C]],
      [B, [A, C]],
      [C, [A, B]],
    ] as const) {
      const dir = unit(sub(center(x), centroid(others.map(center))));
      positions.push({ key: x.id, x: x.cx + dir.x * 0.5 * x.r, y: x.cy + dir.y * 0.5 * x.r });
    }

    for (const [x, y, z] of [
      [A, B, C],
      [A, C, B],
      [B, C, A],
    ] as const) {
      const mid = midpoint(center(x), center(y));
      const dir = unit(sub(mid, center(z)));
      positions.push({
        key: `${x.id}${y.id}`,
        x: mid.x + dir.x * 0.35 * x.r,
        y: mid.y + dir.y * 0.35 * x.r,
      });
    }

    const c = centroid([A, B, C].map(center));
    positions.push({ key: 'ABC', x: c.x, y: c.y });
    positions.push(outsideAnchor(sets));
    return positions;
  }

  const [A, B] = sets as [VennSet<K>, VennSet<K>];
  const mid = midpoint(center(A), center(B));
  return [
    { key: A.id, x: A.cx - 0.45 * A.r, y: A.cy },
    { key: B.id, x: B.cx + 0.45 * B.r, y: B.cy },
    { key: `${A.id}${B.id}`, x: mid.x, y: mid.y },
    outsideAnchor(sets),
  ];
}

// ---------------------------------------------------------------------------
// Member markers — one small circle per element, grouped by region and laid
// out in a compact grid centered on that region's anchor.
// ---------------------------------------------------------------------------

const MEMBER_SPACING = 13;

/** Canonical region key for a member: the ids of `sets` it belongs to, in
 * set-definition order (matches {@link computeRegionPositions}'s keys), or
 * `'none'` when it belongs to no set. */
function regionKeyForMember<K extends string = string>(
  member: VennMember,
  sets: readonly VennSet<K>[],
): string {
  const key = sets
    .filter((s) => member.sets.includes(s.id))
    .map((s) => s.id)
    .join('');
  return key === '' ? 'none' : key;
}

/** Compact grid of `count` points centered on `anchor`, roughly square. */
function regionGridPositions(anchor: Point, count: number): Point[] {
  if (count <= 0) return [];
  const cols = Math.max(1, Math.ceil(Math.sqrt(count)));
  const rows = Math.ceil(count / cols);
  const offsetX = ((cols - 1) * MEMBER_SPACING) / 2;
  const offsetY = ((rows - 1) * MEMBER_SPACING) / 2;
  const positions: Point[] = [];
  for (let i = 0; i < count; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    positions.push({
      x: anchor.x + col * MEMBER_SPACING - offsetX,
      y: anchor.y + row * MEMBER_SPACING - offsetY,
    });
  }
  return positions;
}

/** Renders every member marker; returns the selected member's (cx, cy), or
 * `null` when nothing is selected (or the selected id no longer exists in
 * `config`) — the caller uses this to place the selection tooltip. */
function renderMembers<K extends string = string>(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  config: VennConfig<K>,
  selectedId: string | null,
  onSelect: (member: VennMember) => void,
): Point | null {
  const anchors = new Map(computeRegionPositions(config.sets).map((p) => [p.key, p]));

  const byRegion = new Map<string, VennMember[]>();
  for (const member of config.members) {
    const key = regionKeyForMember(member, config.sets);
    const list = byRegion.get(key);
    if (list) list.push(member);
    else byRegion.set(key, [member]);
  }

  let selectedPos: Point | null = null;

  for (const [key, regionMembers] of byRegion) {
    const anchor = anchors.get(key);
    if (!anchor) continue;
    // Shrink the marker when its region is crowded (e.g. the 200-element
    // custom universe) so the diagram stays legible.
    const r = regionMembers.length > 40 ? 3 : 5;
    const positions = regionGridPositions(anchor, regionMembers.length);
    regionMembers.forEach((member, i) => {
      const pos = positions[i]!;
      if (member.id === selectedId) selectedPos = pos;
      g.append('circle')
        .attr('class', member.id === selectedId ? 'venn-member selected' : 'venn-member')
        .attr('cx', pos.x)
        .attr('cy', pos.y)
        .attr('r', r)
        .on('click', (event: MouseEvent) => {
          event.stopPropagation();
          onSelect(member);
        });
    });
  }

  return selectedPos;
}

// ---------------------------------------------------------------------------
// Selection tooltip — an in-svg label pinned to the clicked marker, replacing
// the page-level info badge (which sat far from the diagram it described).
// ---------------------------------------------------------------------------

const TOOLTIP_HEIGHT = 22;
const TOOLTIP_CHAR_WIDTH = 6.2;
const TOOLTIP_PAD_X = 10;
const TOOLTIP_GAP = 14;

/** Set-membership notation: `${member.label} ∈ ${names}` for members
 * belonging to at least one set, names being the member's sets' translated
 * labelKeys joined with ' ∩ '; `${member.label} ∉ ${allNames}` for members
 * in no set, allNames being ALL of the config's sets' translated labelKeys
 * joined with ' ∪ '. */
function tooltipText<K extends string = string>(
  member: VennMember,
  config: VennConfig<K>,
  i18n: I18n<K>,
): string {
  const names = config.sets
    .filter((s) => member.sets.includes(s.id))
    .map((s) => i18n.t(s.labelKey));
  if (names.length > 0) {
    return `${member.label} ∈ ${names.join(' ∩ ')}`;
  }
  const allNames = config.sets.map((s) => i18n.t(s.labelKey));
  return `${member.label} ∉ ${allNames.join(' ∪ ')}`;
}

/** A rounded-rect + text group centered ~14px above `pos`, clamped so it
 * never leaves the `viewWidth` × `viewHeight` viewBox. Non-interactive
 * (`.venn-tooltip` is `pointer-events: none` in CSS) so it never intercepts
 * clicks meant for markers or the background beneath it. */
function renderTooltip(
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  text: string,
  pos: Point,
  viewWidth: number,
  viewHeight: number,
): void {
  const rectWidth = text.length * TOOLTIP_CHAR_WIDTH + TOOLTIP_PAD_X * 2;
  const x = Math.max(0, Math.min(viewWidth - rectWidth, pos.x - rectWidth / 2));
  const y = Math.max(
    0,
    Math.min(viewHeight - TOOLTIP_HEIGHT, pos.y - TOOLTIP_GAP - TOOLTIP_HEIGHT),
  );

  const tip = g.append('g').attr('class', 'venn-tooltip');
  tip
    .append('rect')
    .attr('class', 'venn-tooltip-bg')
    .attr('x', x)
    .attr('y', y)
    .attr('width', rectWidth)
    .attr('height', TOOLTIP_HEIGHT)
    .attr('rx', 5);
  tip
    .append('text')
    .attr('class', 'venn-tooltip-text')
    .attr('x', x + rectWidth / 2)
    .attr('y', y + TOOLTIP_HEIGHT / 2)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .text(text);
}
